<template>
    <div class="page-ctl-wrapper">
        <ul class="page-items" @click="pageItemClicked">
            <li class="pre" attr-v="pre" v-if="!isSimple"></li>
            <li :class="{ selected: num === currentPage }" v-for="(num, idx) in showingPages" :key="idx" :attr-v="num">{{ num }}</li>
            <li class="next" attr-v="next" v-if="!isSimple"></li>
        </ul>
        <div class="jump-wrapper" v-if="!isSimple">
            <span>{{ $t("zhiji.common.jumpto") }}</span>
            <input class="jump-input" v-model="jumpPage" />
            <a class="jump-btn" @click="jumpToPage">{{ $t("zhiji.common.jump") + ">>" }}</a>
        </div>
    </div>
</template>

<script>
/**
 * emit pageChange
 */

const SHOW_PAGE_NUM = 9; //展示的可选页按钮数量，必须是奇数
const PAGE_MIDDLE_IDX = (SHOW_PAGE_NUM + 1) / 2;
const PAGE_HALF_NUM = (SHOW_PAGE_NUM - 1) / 2;
const NEXT_STEP = 5; //pre和next步长
const ELLIPSIS = "...";

export default {
    name: "PageControl",
    props: ["pageLength", "lengthRank", "total", "curPage", "isSimple"],
    computed: {
        //
    },
    data() {
        return {
            showingPages: [],
            jumpPage: 1,
            currentPage: this.curPage || 1,
            totalPage: Math.ceil(this.total ? this.total / this.pageLength : 0)
        };
    },
    watch: {
        total() {
            this.resetPageCount();
        },
        pageLength() {
            this.resetPageCount();
        },
        curPage(curVal) {
            this.selectPage(curVal);
        }
    },
    mounted() {
        this.initShowingPage();
    },
    methods: {
        initShowingPage() {
            this.setShowingPages(1, this.isPageAllShowed() ? this.totalPage : SHOW_PAGE_NUM);
        },
        setShowingPages(start, end) {
            let newShowingPages = num2numArr(start, end);

            this.addFirstPages(newShowingPages);
            this.addEndPages(newShowingPages);

            this.showingPages.splice(0, this.showingPages.length, ...newShowingPages);
        },
        resetPageCount() {
            this.totalPage = Math.ceil(this.total / this.pageLength);

            let selected = this.currentPage < this.totalPage ? this.currentPage : 1;

            this.selectPage(selected);
        },
        selectPage(page) {
            this.currentPage = page;

            this.setPagesByMiddlePage(page);

            // 触发事件
            this.$emit("pageChange", {
                curPage: this.currentPage,
                pageLength: this.pageLength
            });
        },
        setPagesByMiddlePage(page) {
            if (this.isPageAllShowed()) {
                this.setShowingPages(1, this.totalPage);
                return;
            }

            // 更改页列表展示
            const isAtFirstRect = page <= PAGE_MIDDLE_IDX;
            const isAtEndRect = page >= this.totalPage - SHOW_PAGE_NUM + PAGE_MIDDLE_IDX;
            let startPage = 1;
            let endPage = this.totalPage;

            if (isAtFirstRect) {
                endPage = SHOW_PAGE_NUM;
            } else if (isAtEndRect) {
                startPage = this.totalPage - SHOW_PAGE_NUM + 1;
            } else {
                startPage = page - PAGE_HALF_NUM;
                endPage = startPage + SHOW_PAGE_NUM - 1;
            }

            this.setShowingPages(startPage, endPage);
        },
        isPageAllShowed() {
            return this.totalPage <= SHOW_PAGE_NUM;
        },
        addFirstPages(pages) {
            const isNeedAddFirst = pages.length && pages[0] > 1;
            if (!isNeedAddFirst) {
                return;
            }

            const firstPages = [1, ELLIPSIS];
            pages.splice(0, 2, ...firstPages);
        },
        addEndPages(pages) {
            const isNeedAddEnd = pages.length && pages[pages.length - 1] < this.totalPage;
            if (!isNeedAddEnd) {
                return;
            }

            const endPages = [ELLIPSIS, this.totalPage];
            pages.splice(SHOW_PAGE_NUM - 2, 2, ...endPages);
        },
        pageItemClicked(event) {
            const target = event.target;
            let pageNum;

            if (!(pageNum = target.getAttribute("attr-v")) || pageNum === ELLIPSIS) {
                return;
            }

            if (pageNum === "pre") {
                if (this.currentPage - 1 > 0) {
                    this.selectPage(parseInt(this.currentPage - 1));
                }
            } else if (pageNum === "next") {
                if (this.currentPage + 1 <= this.totalPage) {
                    this.selectPage(parseInt(this.currentPage + 1));
                }
            } else if (pageNum != this.currentPage) {
                this.selectPage(parseInt(pageNum));
            }

            // 单独使用时触发事件
            this.$emit("pageClick", {
                curPage: this.currentPage,
                pageLength: this.pageLength
            });
        },
        preClick() {
            const curShowingMiddlePage = this.showingPages[PAGE_HALF_NUM];
            const firstPage = curShowingMiddlePage - PAGE_HALF_NUM;

            if (firstPage === 1) {
                return;
            }

            this.setPagesByMiddlePage(curShowingMiddlePage - NEXT_STEP);
        },
        nextClick() {
            const curShowingMiddlePage = this.showingPages[PAGE_HALF_NUM];
            const lastPage = curShowingMiddlePage + PAGE_HALF_NUM;

            if (lastPage === this.currentPage) {
                return;
            }

            this.setPagesByMiddlePage(curShowingMiddlePage + NEXT_STEP);
        },
        jumpToPage() {
            this.selectPage(parseInt(this.jumpPage));
        }
    }
};

function num2numArr(num, numEnd) {
    let ret = [];
    let start = numEnd ? num : 1;
    const end = numEnd != null ? numEnd : num;

    for (; start <= end; start++) {
        ret.push(start);
    }

    return ret;
}
</script>

<style lang="scss" scoped>
.page-ctl-wrapper {
    display: inline-block;
    position: relative;

    .page-items {
        display: inline-block;
        vertical-align: text-bottom;
        color: rgba(65, 71, 94, 0.7);

        li {
            font-size: 12px;
            float: left;
            margin-right: 8px;
            min-width: 28px;
            padding: 0 3px;
            height: 28px;
            line-height: 26px;
            text-align: center;
            box-sizing: border-box;
            border: 1px solid #d8e4f0;
            border-radius: 4px;
            background: #fff;
            cursor: pointer;

            &:hover {
                background: #dee9f2;
                border-color: #aec5d8;
            }
        }

        .selected {
            background: #dee9f2;
            color: rgba(65, 71, 94, 1);
            border-color: #aec5d8;
        }

        .pre {
            &:before {
                background: url($zhijiStaticImagePath+"class-spr.png") no-repeat -168px -21px;
                transform: rotate(180deg);
            }

            &:hover:before {
                background-position: -178px -21px;
            }
        }

        .next {
            &:before {
                background: url($zhijiStaticImagePath+"class-spr.png") no-repeat -168px -21px;
            }

            &:hover:before {
                background-position: -178px -21px;
            }
        }

        .pre:before,
        .next:before {
            width: 9px;
            height: 13px;
            content: "";
            display: inline-block;
            vertical-align: -2px;
        }
    }

    .jump-wrapper {
        display: inline-block;
        vertical-align: text-bottom;
        margin-left: 16px;
        .jump-input {
            border: 1px solid #d8e4f0;
            width: 40px;
            padding: 5px 6px 5px 6px;
            font-size: 12px;
            margin-right: 8px;
            border-radius: 4px;
            &:hover {
                border-color: #2088f3;
            }
        }

        .jump-btn {
            display: inline-block;
            border: 1px solid #d8e4f0;
            padding: 5px 18px 5px;
            border-radius: 4px;
            font-size: 12px;
            background: #fff;
            cursor: pointer;

            &:hover {
                background: #dee9f2;
                border-color: #aec5d8;
            }
        }
    }
}
</style>
